// src/components/YourPath/ProjectScoresList.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  IconButton,
  Autocomplete,
} from '@mui/material';
import { Eye } from 'lucide-react';
import { Project } from '../../../types/project.types';
import { JudgeAssignmentStatus } from '../../../types/judging.types';
import {
  //fetchScoresByProjectIds,
  computeStats,
  subscribeToProjectScores,
} from '../../../services/projectScores.service';
import { fetchFairs, Fair } from '../../../services/fair.service';
import ProjectScoresDialog from './ProjectScoresDialog';

export interface ProjectWithStatus extends Project {
  fairId: string; // ← tell TS that each project has a fairId
  judgingStatus: JudgeAssignmentStatus | null;
  averageScore: number | null;
  judgeCount: number;
}

interface ProjectScoringListProps {
  projects?: Project[];
}

const ProjectScoresList: React.FC<ProjectScoringListProps> = ({
  projects: passedProjects,
}) => {
  const [projects, setProjects] = useState<ProjectWithStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fair dropdown state
  const [fairs, setFairs] = useState<Fair[]>([]);
  const [selectedFairId, setSelectedFairId] = useState<string | null>(null);
  const [fairSearchTerm, setFairSearchTerm] = useState<string>('');

  // Status filter
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'scored' | 'unscored'
  >('all');

  // Dialog state for viewing project scores
  const [selectedProject, setSelectedProject] =
    useState<ProjectWithStatus | null>(null);
  const [isScoresDialogOpen, setIsScoresDialogOpen] = useState(false);

  // Load projects & stats with real-time updates
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    if (!passedProjects || passedProjects.length === 0) {
      setProjects([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const projectIds = passedProjects.map((p) => p.id);
    unsubscribe = subscribeToProjectScores(projectIds, (rawScores) => {
      // Compute stats whenever scores change
      const stats = computeStats(rawScores);

      // Update projects with new stats
      const withStats: ProjectWithStatus[] = passedProjects.map((p) => ({
        ...p,
        fairId: (p as any).fairId,
        averageScore: stats[p.id]?.averageScore ?? null,
        judgeCount: stats[p.id]?.judgeCount ?? 0,
        judgingStatus: (p as any).judgingStatus ?? null,
      }));

      setProjects(withStats);
      setLoading(false);
    });

    // Return cleanup function
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [passedProjects]);

  // Load fairs list
  useEffect(() => {
    fetchFairs()
      .then(setFairs)
      .catch((err) => console.error('Error loading fairs:', err));
  }, []);

  // Derive up to 5 fair options, filtered by what the user types
  const fairOptions = (
    fairSearchTerm
      ? fairs.filter((f) =>
          f.name.toLowerCase().includes(fairSearchTerm.toLowerCase())
        )
      : fairs
  ).slice(0, 5);

  // Filter projects by selected fairId and status
  const filteredProjects = projects
    .filter((p) => !selectedFairId || p.fairId === selectedFairId)
    .filter((p) => {
      switch (statusFilter) {
        case 'scored':
          return p.judgeCount > 0;
        case 'unscored':
          return p.judgeCount === 0;
        default:
          return true;
      }
    });

  const handleView = (project: ProjectWithStatus) => {
    setSelectedProject(project);
    setIsScoresDialogOpen(true);
  };
  return (
    <Box>
      <Typography variant="h4" gutterBottom color="primary" fontWeight="bold">
        Project Scoring
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters: Fair selector + Status */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Autocomplete
            size="small"
            options={fairOptions}
            getOptionLabel={(f) => f.name}
            value={fairs.find((f) => f.id === selectedFairId) || null}
            onChange={(_, f) => setSelectedFairId(f?.id ?? null)}
            inputValue={fairSearchTerm}
            onInputChange={(_, v) => setFairSearchTerm(v)}
            renderInput={(params) => (
              <TextField {...params} label="Select Fair" />
            )}
            sx={{ flexGrow: 1 }}
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) =>
                setStatusFilter(e.target.value as typeof statusFilter)
              }
            >
              <MenuItem value="all">All Projects</MenuItem>
              <MenuItem value="scored">Scored</MenuItem>
              <MenuItem value="unscored">Not Scored</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Projects Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Project Name</TableCell>
              <TableCell align="center">Avg. Score</TableCell>
              <TableCell align="center">Judges Count</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Loading projects...
                </TableCell>
              </TableRow>
            ) : filteredProjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No projects found
                </TableCell>
              </TableRow>
            ) : (
              filteredProjects.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.name}</TableCell>
                  <TableCell align="center">
                    {p.averageScore != null ? p.averageScore.toFixed(1) : '—'}
                  </TableCell>
                  <TableCell align="center">{p.judgeCount}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleView(p)}>
                      <Eye size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Project Scores Dialog */}
      {selectedProject && (
        <ProjectScoresDialog
          open={isScoresDialogOpen}
          onClose={() => {
            setIsScoresDialogOpen(false);
            setSelectedProject(null);
          }}
          project={selectedProject}
        />
      )}
    </Box>
  );
};

export default ProjectScoresList;
